import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import { getThemeTextColor } from "../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import axios from "axios";

const CountryRequirementsSection = ({ requirements, onChange }) => {
  const theme = useTheme();
  const [countries, setCountries] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newRequirement, setNewRequirement] = useState({
    countryId: "",
    hasTimeRestriction: false,
    specialRequirements: "",
  });

  // Debug logging for requirements prop
  React.useEffect(() => {
    // Debug logging removed - functionality is working correctly
  }, [requirements]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/api/v1/countries");
        setCountries(response.data);
        setError("");
      } catch (error) {
        console.error("Error fetching countries:", error);
        setError("Failed to load countries. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const handleAddRequirement = () => {
    if (!newRequirement.countryId) {
      setError("Please select a country");
      return;
    }

    const country = countries.find((c) => c.countryId === newRequirement.countryId);
    const requirement = {
      ...newRequirement,
      countryName: country?.countryName,
      id: Date.now(), // Temporary ID for frontend
    };

    onChange([...requirements, requirement]);
    setAddDialogOpen(false);
    setNewRequirement({
      countryId: "",
      hasTimeRestriction: false,
      specialRequirements: "",
    });
    setError("");
  };

  const handleRemoveRequirement = (id) => {
    onChange(requirements.filter((r) => r.id !== id));
  };

  const getTimeRestrictionLabel = (hasTimeRestriction) => {
    return hasTimeRestriction ? "Time Restriction" : "No Time Restriction";
  };

  const getAsteriskDescription = (specialRequirements) => {
    switch (specialRequirements) {
      case "1":
        return "Optional legalization";
      case "2":
        return "Required legalization";
      case "3":
        return "Required legalization + required sworn translation of CoC documents";
      case "4":
        return "Required sworn translation of CoC documents";
      default:
        return "No special requirement";
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Typography sx={{ ...getThemeTextColor(theme) }}>Loading countries...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0",
        borderRadius: 1,
        backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#fafafa",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Country-Specific Requirements
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define exceptions for specific countries. These override the default settings above.
      </Typography>

      {requirements.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          {requirements.map((req) => {
            return (
              <Chip
                key={req.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" component="span">
                      {req.countryName}
                    </Typography>
                    {req.hasTimeRestriction && (
                      <>
                        <Typography variant="body2" component="span" color="text.secondary">
                          •
                        </Typography>
                        <Typography variant="body2" component="span" sx={{ color: "warning.main" }}>
                          {getTimeRestrictionLabel(req.hasTimeRestriction)}
                        </Typography>
                      </>
                    )}

                    {req.specialRequirements && (
                      <>
                        <Typography variant="body2" component="span" color="text.secondary">
                          •
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            fontWeight: "bold",
                            color: "primary.main",
                            backgroundColor: "primary.50",
                            px: 0.5,
                            py: 0.1,
                            borderRadius: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          {getAsteriskDescription(req.specialRequirements)}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
                onDelete={() => handleRemoveRequirement(req.id)}
                color="primary"
                variant="outlined"
                sx={{ mb: 1 }}
                title={req.specialRequirements ? `Special requirement: ${getAsteriskDescription(req.specialRequirements)}` : undefined}
              />
            );
          })}
        </Stack>
      )}

      <Button variant="outlined" onClick={() => setAddDialogOpen(true)} size="small" disabled={countries.length === 0}>
        Add Country Requirement
      </Button>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Country Requirement</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "white",
                px: 0.5,
                transform: "translate(14px, -9px) scale(0.75)",
                transformOrigin: "top left",
              }}
            >
              Select Country
            </InputLabel>
            <Select
              value={newRequirement.countryId}
              onChange={(e) => setNewRequirement({ ...newRequirement, countryId: e.target.value })}
              sx={{ mt: 1 }}
              displayEmpty
              renderValue={(value) => {
                if (value === "") return "No country selected";
                const country = countries.find((c) => c.countryId === value);
                return country ? `${country.countryName} (${country.countryCode})` : "No country selected";
              }}
            >
              {countries.map((country) => (
                <MenuItem key={country.countryId} value={country.countryId}>
                  {country.countryName} ({country.countryCode})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={newRequirement.hasTimeRestriction}
                onChange={(e) =>
                  setNewRequirement({
                    ...newRequirement,
                    hasTimeRestriction: e.target.checked,
                  })
                }
              />
            }
            label="Time Restriction (Optional)"
            sx={{ mt: 2 }}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "white",
                px: 0.5,
                transform: "translate(14px, -9px) scale(0.75)",
                transformOrigin: "top left",
              }}
              shrink={true}
            >
              Special Requirements
            </InputLabel>
            <Select
              value={newRequirement.specialRequirements}
              onChange={(e) => setNewRequirement({ ...newRequirement, specialRequirements: e.target.value })}
              margin="normal"
              sx={{ mt: 1 }}
              displayEmpty
              renderValue={(value) => {
                if (value === "") return "No special requirements";
                if (value === "1") return "Optional legalization";
                if (value === "2") return "Required legalization";
                if (value === "3") return "Required legalization + required sworn translation of CoC documents";
                if (value === "4") return "Required sworn translation of CoC documents";
                return value;
              }}
            >
              <MenuItem value="">No special requirements</MenuItem>
              <MenuItem value="1">Optional legalization</MenuItem>
              <MenuItem value="2">Required legalization</MenuItem>
              <MenuItem value="3">Required legalization + required sworn translation of CoC documents</MenuItem>
              <MenuItem value="4">Required sworn translation of CoC documents</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Select the special requirement level. Higher levels indicate stricter legalization requirements.
            </Typography>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddRequirement}
            variant="contained"
            disabled={!newRequirement.countryId || newRequirement.specialRequirements === ""}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CountryRequirementsSection;
